<script setup>
import { onBeforeUnmount } from 'vue'
import NeonDigit from './NeonDigit.vue'
import ThxIntro from './ThxIntro.vue'
import { frames } from '../../../../utils/frames'

const INTRO_DURATION = 4.5

const gsapTimeline = useGsapTimeline()

const { item } = defineProps({
  item: Object,
})

const outputElement = ref(null)
const outputSize = ref({ width: 0, height: 0 })
const stageSize = computed(() => {
  const width = Math.min(outputSize.value.width, outputSize.value.height * (16 / 9))
  return { width, height: width * (9 / 16) }
})
const stageUnit = computed(() => stageSize.value.width / 100)
const isCornerScreen = computed(() => outputSize.value.width === 2160 && outputSize.value.height === 3840)
const stageStyle = computed(() => ({
  width: `${stageSize.value.width}px`,
  height: `${stageSize.value.height}px`,
  left: `${outputSize.value.width / 2}px`,
  top: `${outputSize.value.height * (isCornerScreen.value ? 0.4 : 0.5)}px`,
  transform: 'translate(-50%, -50%)',
}))
const stageVariables = computed(() => ({
  '--stage-unit': `${stageUnit.value}px`,
  '--copy-font-size': `${stageUnit.value * (isCornerScreen.value ? 12 : 6)}px`,
  '--copy-max-width': `${stageSize.value.width * 0.9}px`,
}))

const total = computed(() => item.enrichment?.total ?? null)
const formatted = computed(() => (total.value === null ? '---' : String(total.value).replace(/\B(?=(\d{3})+(?!\d))/g, '.')))
const cells = computed(() => [...formatted.value].map((char) => (char === '.' || char === '-' ? { symbol: char, digit: 0 } : { symbol: null, digit: Number(char) })))
const dots = computed(() => cells.value.filter((cell) => cell.symbol === '.').length)
const fontSize = computed(() => {
  const characterCount = Math.max(formatted.value.length - dots.value * 0.5, 3)
  const widthLimit = stageUnit.value * 90
  const heightLimit = stageUnit.value * (isCornerScreen.value ? 80 : 34.5)
  return `${Math.min(widthLimit / (characterCount * 0.4), heightLimit)}px`
})

const lit = ref(false)

const numberElement = ref(null)
const headingElement = ref(null)
const copyElement = ref(null)
const introReady = ref(false)
const overlaysReady = ref(false)
let outputResizeObserver
let outputResizeFallback

const timeline = gsapTimeline({ paused: true, delay: frames(15) })

const startTimeline = () => {
  introReady.value = true
  const debugStore = useDebugStore()
  debugStore.timelines = debugStore.timelines.filter((entry) => entry.timeline !== timeline)
  debugStore.add('ThxToYouCounter', timeline)
  if (overlaysReady.value) timeline.play(0)
}

onMounted(() => {
  const updateOutputSize = () => {
    if (!outputElement.value) return
    outputSize.value = {
      width: outputElement.value.clientWidth,
      height: outputElement.value.clientHeight,
    }
  }

  updateOutputSize()
  if ('ResizeObserver' in window) {
    outputResizeObserver = new ResizeObserver(updateOutputSize)
    outputResizeObserver.observe(outputElement.value)
  } else {
    outputResizeFallback = updateOutputSize
    window.addEventListener('resize', outputResizeFallback)
  }

  timeline
    .call(() => (lit.value = false), null, 0)
    .set(numberElement, { opacity: 0 }, 0)
    .set(headingElement, { opacity: 0 }, 0)
    .set(copyElement, { opacity: 0 }, 0)
    .to(numberElement, { opacity: 1, duration: 0.8, ease: 'none' }, INTRO_DURATION - 0.95)
    .fromTo(headingElement, { opacity: 0, yPercent: -10 }, { opacity: 1, yPercent: 0, duration: 0.35, ease: 'power2.out' }, INTRO_DURATION)
    .fromTo(copyElement, { opacity: 0, yPercent: 10 }, { opacity: 1, yPercent: 0, duration: 0.35, ease: 'power2.out' }, INTRO_DURATION + 0.15)
    .call(() => (lit.value = true), null, INTRO_DURATION)
  overlaysReady.value = true
  if (introReady.value) timeline.play(0)
})

onBeforeUnmount(() => {
  outputResizeObserver?.disconnect()
  if (outputResizeFallback) window.removeEventListener('resize', outputResizeFallback)
})
</script>

<template>
  <div ref="outputElement" class="thx-counter relative size-full overflow-clip bg-[#6c0000] text-white isolate" :style="stageVariables">
    <div class="thx-backdrop pointer-events-none absolute inset-0" aria-hidden="true" />
    <ThxIntro class="absolute inset-0" :timeline="timeline" :stageStyle="stageStyle" :stageUnit="stageUnit" :isCornerScreen="isCornerScreen" @ready="startTimeline" />

    <div class="thx-stage absolute z-[4]" :style="stageStyle">
      <div class="thx-counter__content absolute left-1/2 top-1/2 z-[1] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
        <div v-show="!isCornerScreen" ref="headingElement" class="thx-copy thx-copy--above font-cervo">Gerben heeft al</div>
        <p ref="numberElement" class="font-cervo m-0 min-h-[1em] whitespace-nowrap leading-none tabular-nums opacity-0" :style="{ fontSize }">
          <!-- Keyed from the right, so existing digits keep their state when the number gets a digit more. -->
          <NeonDigit v-for="(cell, index) in cells" :key="cells.length - index" :digit="cell.digit" :symbol="cell.symbol" :index="index" :lit="lit" />
        </p>
        <div ref="copyElement" class="thx-copy thx-copy--below font-cervo">mensen bedankt!</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.thx-backdrop {
  background: radial-gradient(ellipse at 50% 45%, rgb(255 40 40 / 28%), transparent 65%), radial-gradient(ellipse at 50% 120%, rgb(0 0 0 / 55%), transparent 60%);
}

.thx-copy {
  max-width: var(--copy-max-width);
  margin: 0.05em 0 0;
  font-size: var(--copy-font-size);
  font-weight: 700;
  overflow-wrap: anywhere;
  white-space: pre-line;
  color: #fff;
  text-align: center;
  text-shadow: 0 0 6px #fff, 0 0 14px #ff3b2a, 0 0 32px #ed3524;
}

.thx-copy--above {
  margin: 0 0 0.05em;
}
</style>
